import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: "1 1 auto",
    minWidth: 0,
    maxWidth: "none",
    display: "flex",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      flex: "1 1 auto",
    },
  },
  ticketActionButtons: {
    flex: "0 0 auto",
    display: "flex",
    minWidth: 0,
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 auto",
      justifyContent: "flex-end",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [showInternalEvents, setShowInternalEvents] = useState(false);
  const ticketStatusRef = useRef();

  useEffect(() => {
    setLoading(true);
    setShowInternalEvents(false);
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);

        setContact(data.contact);
        setTicket(data);
        ticketStatusRef.current = data.status;
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    fetchTicket();
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const joinTicketRoom = () => {
      socket.emit("joinChatBox", ticketId);
    };

    socket.on("connect", joinTicketRoom);

    if (socket.connected) {
      joinTicketRoom();
    }

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        const incomingTicketId = Number(data?.ticket?.id);

        if (!Number.isFinite(incomingTicketId) || incomingTicketId !== Number(ticketId)) {
          return;
        }

        const previousStatus = ticketStatusRef.current;
        ticketStatusRef.current = data.ticket.status;

        setTicket((prevState) => ({
          ...prevState,
          ...data.ticket,
          queue: {
            ...(prevState?.queue || {}),
            ...(data?.ticket?.queue || {}),
          },
          whatsapp: {
            ...(prevState?.whatsapp || {}),
            ...(data?.ticket?.whatsapp || {}),
          },
        }));

        if (data?.ticket?.contact) {
          setContact((prevState) => ({
            ...prevState,
            ...data.ticket.contact,
          }));
        }

        if (previousStatus !== "closed" && data?.ticket?.status === "closed") {
          history.push("/tickets", {
            switchTab: previousStatus === "pending" ? "pending" : "open",
          });
        }
      }

      if (data.action === "delete") {
        const incomingTicketId = Number(data?.ticketId || data?.ticket?.id);

        if (!Number.isFinite(incomingTicketId) || incomingTicketId !== Number(ticketId)) {
          return;
        }

        toast.success("Atendimento removido com sucesso.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons
              ticket={ticket}
              showInternalEvents={showInternalEvents}
              onToggleInternalEvents={() =>
                setShowInternalEvents(prevState => !prevState)
              }
            />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            showInternalEvents={showInternalEvents}
          ></MessagesList>
          <MessageInput
            ticketStatus={ticket.status}
            ticket={ticket}
            onInternalNoteSent={() => setShowInternalEvents(true)}
          />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
